<script lang="ts" setup>
import { onMounted, onUnmounted, watchEffect } from 'vue'
import { Dialog, DialogContent, DialogClose, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import type { PageCardType } from '~/types/Page'

const isOpen = ref(false)
const step = ref(1)
const eventBus = useEventBus()
const userStore = useUserStore()
const globalStore = useGlobalStore()

async function openModal() {
	if (process.server || !userStore.user) {
		return
	}
	setTimeout(() => {
		if (!newDocuments.value.length) {
			return
		}
		isOpen.value = true
	}, 500)
}

const newDocuments = computed(() => {
	return Object.values(globalStore.rule_pages).filter((document: PageCardType | null) => {
		if(!document || !document.published_date) return false
		const publishedDate = new Date(document.published_date)
		const userDate = new Date(userStore.user?.accept_rules_date || '2024-01-01')
		return publishedDate > userDate
	})
})

watchEffect(async () => {
	if (process.client) {
		eventBus.on('maybeOpenAcceptRulesModal', async () => {
			await openModal()
		})
	}
})

async function acceptRules() {
	await userStore.updateAcceptRulesDate()
	isOpen.value = false
}
</script>

<template>
  <Dialog v-model:open="isOpen">
		<DialogContent class="ys-modal-dialog w-full overflow-hidden border-none" :class="{'!bg-black': step !== 1}">
			<DialogClose class="absolute top-4 ltr:right-4 rtl:left-4">
				<Icon name="ri:close-line" size="20px" class="text-gray-500 translate-all hover:text-gray-700" />
			</DialogClose>
			<div class="p-6">
				<DialogHeader>
					<DialogTitle class="text-lg font-bold">
						{{ $t('Important documents updated') }}
					</DialogTitle>
					<DialogDescription class="text-sub mb-6">
						{{ $t('We have updated our documents. Please review the changes and confirm your agreement:') }}
						<ul class="list-disc list-inside mt-3">
							<li v-for="document in newDocuments" :key="document?.documentId">
								<NuxtLink 
									v-if="document?.documentId && document?.name"
									:to="{ name: 'page', params: { id: document.documentId } }" 
									class="text-primary-900 hover:text-primary-700 underline"
								>
									{{ document.name }}
								</NuxtLink>
							</li>
						</ul>
						<p class="text-sm text-gray-600 mt-4">
							{{ $t('By clicking "Accept", you confirm that you have reviewed the updated documents and agree to their terms.') }}
						</p>
					</DialogDescription>
				</DialogHeader>

				<DialogFooter class="flex flex-col gap-4">
					<Button variant="primary" class="w-full" @click="acceptRules">
						{{ $t('Accept') }}
					</Button>
				</DialogFooter>
			</div>
		</DialogContent>
	</Dialog>
</template>